<template>
  <Section>
    <Error
      v-if="!$apollo.loading && errors.length"
      message="It looks like there was an issue loading the template."
      :errors="errors"
      :error-action="onRetryClick"
      error-action-label="Retry?"
      class="max-w-md mx-auto mb-4 md:mb-8 lg:mb-12"
    />

    <Loader v-if="$apollo.loading" class="mt-6" label="Loading Template..." />

    <div
      v-if="!$apollo.loading && !errors.length"
      class="flex flex-wrap mb-6 bg-white rounded-lg md:p-6 md:mb-8 min-h-screen"
    >
      <div class="p-4 pb-0 meta-holder">
        <TemplateMeta :template="template" />
      </div>

      <p class="preview-title">
        Preview
      </p>

      <!-- Preview Holder -->
      <div class="mx-2 preview-holder">
        <Preview
          :query-input="previewQueryInput"
          @preview-loaded="onPreviewLoaded"
        />
      </div>
    </div>

    <div class="mt-2 mb-4 divider md:mt-4 lg:mt-6" />
  </Section>
</template>

<script>
import Section from "@/components/UI/Section";
import { BaseTemplate } from "@/graphql/queries";
import Preview from "@/components/Editor/Preview.vue";
import Loader from "@/components/UI/Loader";
import Error from "@/components/UI/Error";
import TemplateMeta from "@/components/Templates/TemplateMeta";
import { getAccountId, isAuthError } from "@/lib/auth";

export default {
  apollo: {
    template: {
      query: BaseTemplate,
      update: (data) => data.baseTemplate,
      variables: function() {
        return {
          input: { baseTemplateId: this.$route.params.id },
        };
      },
      result({ loading, data }) {
        if (!loading && data?.baseTemplate) {
          this.sendTrackingEvents();
        }
      },
      error({ graphQLErrors }) {
        if (graphQLErrors.length) {
          // If we have an auth error return false here as the login modal should open...
          if (isAuthError(graphQLErrors)) return false;

          this.errors = graphQLErrors.map(({ message }) => message);
        }
      },
    },
  },
  name: "Template",
  components: { Error, Loader, Preview, Section, TemplateMeta },
  data() {
    return {
      errors: [],
      template: null,
    };
  },
  computed: {
    previewQueryInput() {
      return {
        baseTemplateId: this.$route.params.id,
        includeData: true,
      };
    },
  },
  mounted() {
    // Make sure the user is at the top of the screen as sometimes the browser remembers their last known scroll postion
    window.scrollTo(0, 0);
  },
  methods: {
    onPreviewLoaded(previewUrl) {
      // Track the preview loaded event...
      this.$gtm.trackEvent({
        event: "preview-basetemplate-rendered",
        accountId: getAccountId(),
        previewUrl,
      });
    },
    onRetryClick() {
      this.errors = [];
      this.$apollo.queries.template.refetch();
    },
    sendTrackingEvents() {
      this.$gtm.trackEvent({
        event: "basetemplate-previewed",
        accountId: getAccountId(),
        baseTemplateId: this.$route.params.id,
      });

      this.$gtm.trackEvent({
        event: "category-previewed",
        accountId: getAccountId(),
        categoryId: this.template?.category?.categoryId ?? "",
      });
    },
  },
};
</script>

<style scoped>
.meta-holder {
  @apply w-full;

  @screen md {
    @apply w-gr-small pr-4 mb-4;
  }

  @screen lg {
    @apply pr-8;
  }
}

.preview-title {
  @apply w-full mb-2 text-center text-xs text-grey-dark italic;

  @screen md {
    @apply hidden;
  }
}

.preview-holder {
  @apply bg-brand-light border border-grey-light rounded max-h-full max-w-full overflow-auto relative mb-4 w-full h-screen;

  @screen md {
    @apply flex-1 h-auto;
    max-height: none;
  }

  @screen xl {
    @apply flex-grow;
  }
}
</style>
